// components/BookingPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardMedia,
  CircularProgress,
  Snackbar,
  styled,
  alpha,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Person,
  CalendarToday,
  CreditCard,
  CheckCircle,
  Payment,
  EventAvailable,
  Star,
  Wifi,
  AcUnit,
  LocalParking,
  Restaurant,
  Pool,
  FitnessCenter,
  Spa,
  LocalBar,
  RoomService,
  BusinessCenter,
  SupportAgent,
  LocalLaundryService,
  Pets,
  BeachAccess,
} from '@mui/icons-material';
import TopBar from './TopBar';
import NavigationBar from './NavigationBar';
import Footer from './Footer';
import axios from 'axios';

// API base URL and Hotel Code
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const HOTEL_CODE = process.env.REACT_APP_HOTEL_CODE;
const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

// Modern Styled Components
const GlassCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  borderRadius: '24px',
  boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  fontWeight: '700',
  borderRadius: '16px',
  padding: '12px 32px',
  textTransform: 'none',
  fontSize: '1.1rem',
  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
  },
  '&:disabled': {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.5)} 0%, ${alpha(theme.palette.primary.dark, 0.5)} 100%)`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.9),
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: '600',
  },
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  '& .MuiStepLabel-root .Mui-completed': {
    color: theme.palette.primary.main,
  },
  '& .MuiStepLabel-root .Mui-active': {
    color: theme.palette.primary.main,
  },
  '& .MuiStepLabel-label': {
    fontWeight: '700',
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  '& .MuiStepIcon-root': {
    width: 32,
    height: 32,
    '&.Mui-completed': {
      color: theme.palette.primary.main,
    },
    '&.Mui-active': {
      color: theme.palette.primary.main,
    },
  },
}));

const FeatureChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.dark,
  fontWeight: '600',
  borderRadius: '8px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

// Memoized countries list
const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'Japan', 'China',
  'India', 'Brazil', 'Mexico', 'Italy', 'Spain', 'South Korea', 'United Arab Emirates', 'Nigeria',
  'South Africa', 'Egypt', 'Kenya', 'Ghana', 'Other'
];

// Amenity icons mapping
const AMENITY_ICONS = {
  'wifi': <Wifi />,
  'wi-fi': <Wifi />,
  'free wifi': <Wifi />,
  'air conditioning': <AcUnit />,
  'ac': <AcUnit />,
  'parking': <LocalParking />,
  'free parking': <LocalParking />,
  'restaurant': <Restaurant />,
  'pool': <Pool />,
  'swimming pool': <Pool />,
  'breakfast': <Restaurant />,
  'gym': <FitnessCenter />,
  'fitness center': <FitnessCenter />,
  'spa': <Spa />,
  'bar': <LocalBar />,
  'room service': <RoomService />,
  'business center': <BusinessCenter />,
  'concierge': <SupportAgent />,
  'laundry': <LocalLaundryService />,
  'pet friendly': <Pets />,
  'beach access': <BeachAccess />,
  'ocean view': <BeachAccess />,
};

const BookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const room = location.state?.room;
  
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Guest auto-population states
  const [searchingGuest, setSearchingGuest] = useState(false);
  const [foundGuests, setFoundGuests] = useState([]);
  const [guestSearchDialog, setGuestSearchDialog] = useState(false);
  const [selectedExistingGuest, setSelectedExistingGuest] = useState(null);
  
  // Paystack payment state - FIX: Initialize as true and handle script loading separately
  const [paystackInitialized, setPaystackInitialized] = useState(true);
  const [paystackLoadError, setPaystackLoadError] = useState(false);

  const steps = ['Guest Details', 'Review & Pay', 'Confirmation'];
  
  const [formData, setFormData] = useState({
    // Guest Information
    title: 'Mr',
    firstName: '',
    lastName: '',
    middleName: '',
    gender: '',
    nationality: '',
    
    // Contact Information
    email: '',
    phone: '',
    address: '',
    country: '',
    
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactPhone: '',
    
    // Preferences
    preferences: '',
    
    // Booking Information
    checkInDate: '',
    checkOutDate: '',
    numberOfGuests: 1,
    numberOfRooms: 1,
    specialRequests: '',
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [roomAvailable, setRoomAvailable] = useState(null);

  // Memoized calculations
  const nights = useMemo(() => {
    if (formData.checkInDate && formData.checkOutDate) {
      const checkIn = new Date(formData.checkInDate);
      const checkOut = new Date(formData.checkOutDate);
      
      if (isNaN(checkIn) || isNaN(checkOut)) return 0;
      
      const diffTime = Math.abs(checkOut - checkIn);
      const calculatedNights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return calculatedNights > 0 ? calculatedNights : 0;
    }
    return 0;
  }, [formData.checkInDate, formData.checkOutDate]);

  const total = useMemo(() => {
    const roomPrice = room?.price || room?.price_per_night || room?.rate || 0;
    
    if (!roomPrice || nights <= 0) return 0;
    
    return roomPrice * nights;
  }, [room?.price, room?.price_per_night, room?.rate, nights]);

  // Load Paystack script - FIX: Optimized loading with better error handling
  useEffect(() => {
    let mounted = true;
    
    const loadPaystackScript = async () => {
      if (window.PaystackPop) {
        if (mounted) setPaystackInitialized(true);
        return;
      }

      try {
        // Check if script is already being loaded
        const existingScript = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');
        if (existingScript) {
          // Wait for existing script to load
          await new Promise((resolve) => {
            existingScript.onload = resolve;
            existingScript.onerror = resolve;
          });
        } else {
          // Load new script
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://js.paystack.co/v1/inline.js';
            script.async = true;
            script.onload = () => {
              if (mounted) {
                setPaystackInitialized(true);
                setPaystackLoadError(false);
              }
              resolve();
            };
            script.onerror = () => {
              if (mounted) {
                setPaystackInitialized(false);
                setPaystackLoadError(true);
              }
              reject(new Error('Failed to load Paystack script'));
            };
            document.head.appendChild(script);
          });
        }
      } catch (error) {
        console.error('Error loading Paystack:', error);
        if (mounted) {
          setPaystackInitialized(false);
          setPaystackLoadError(true);
        }
      }
    };

    loadPaystackScript();

    return () => {
      mounted = false;
    };
  }, []);

  // Format date to YYYY-MM-DD for API
  const formatDateForAPI = useCallback((dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }, []);

  // API service functions with optimizations
  const api = useMemo(() => ({
    createBooking: async (bookingData) => {
      const response = await axios.post(`${API_BASE_URL}/hotel/staff/reception/create.php`, bookingData, {
        timeout: 15000,
      });
      return response.data;
    },

    processPayment: async (paymentData) => {
      return new Promise((resolve, reject) => {
        if (!window.PaystackPop) {
          reject(new Error('Paystack not loaded'));
          return;
        }

        const handler = window.PaystackPop.setup({
          key: PAYSTACK_PUBLIC_KEY,
          email: paymentData.email,
          amount: paymentData.amount,
          currency: 'NGN',
          ref: 'PSK_' + Math.random().toString(36).substr(2, 9).toUpperCase() + '_' + Date.now(),
          metadata: {
            custom_fields: [
              {
                display_name: "Guest Name",
                variable_name: "guest_name",
                value: `${paymentData.firstName} ${paymentData.lastName}`
              },
              {
                display_name: "Room Type",
                variable_name: "room_type",
                value: paymentData.roomName
              },
              {
                display_name: "Hotel Code",
                variable_name: "hotel_code",
                value: HOTEL_CODE
              }
            ]
          },
          callback: function(response) {
            resolve({
              success: true,
              transactionId: response.transaction,
              reference: response.reference,
              message: 'Payment successful'
            });
          },
          onClose: function() {
            reject(new Error('Payment cancelled by user'));
          }
        });
        
        handler.openIframe();
      });
    },

    verifyPayment: async (reference) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/verify-payment.php`, {
          params: { reference },
          timeout: 10000
        });
        
        console.log('Verification response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Payment verification failed:', error);
        throw new Error(error.response?.data?.error || 'Payment verification failed');
      }
    },

    searchGuests: async (searchTerm) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/guests.php`, {
          params: { 
            hotel_code: HOTEL_CODE,
            search: searchTerm,
            limit: 10
          },
          timeout: 8000
        });
        return response.data;
      } catch (error) {
        console.error('Error searching guests:', error);
        return { success: false, error: 'Failed to search guests' };
      }
    },
  }), []);

  // Debounced guest search with proper cleanup
  useEffect(() => {
    let mounted = true;
    
    const searchExistingGuests = async () => {
      const searchTerm = formData.email || formData.phone;
      if (!searchTerm || searchTerm.length < 4) {
        if (mounted) setFoundGuests([]);
        return;
      }

      if (mounted) setSearchingGuest(true);
      try {
        const result = await api.searchGuests(searchTerm);
        if (mounted && result.success && result.guests && result.guests.length > 0) {
          setFoundGuests(result.guests);
          if (result.guests.length === 1) {
            handleSelectGuest(result.guests[0]);
          } else {
            setGuestSearchDialog(true);
          }
        } else if (mounted) {
          setFoundGuests([]);
        }
      } catch (error) {
        console.error('Error searching guests:', error);
        if (mounted) setFoundGuests([]);
      } finally {
        if (mounted) setSearchingGuest(false);
      }
    };

    const timeoutId = setTimeout(searchExistingGuests, 500);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [formData.email, formData.phone, api]);

  // Check room availability with proper cleanup
  useEffect(() => {
    let mounted = true;

    const checkAvailability = async () => {
      if (!formData.checkInDate || !formData.checkOutDate) return;

      const checkIn = new Date(formData.checkInDate);
      const checkOut = new Date(formData.checkOutDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (checkIn < today) {
        if (mounted) setErrors(prev => ({ ...prev, checkInDate: 'Check-in date cannot be in the past' }));
        return;
      }

      if (checkOut <= checkIn) {
        if (mounted) setErrors(prev => ({ ...prev, checkOutDate: 'Check-out date must be after check-in date' }));
        return;
      }

      if (mounted) setCheckingAvailability(true);
      try {
        const formattedCheckIn = formatDateForAPI(formData.checkInDate);
        const formattedCheckOut = formatDateForAPI(formData.checkOutDate);
        
        const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/availability.php`, {
          params: {
            hotel_code: HOTEL_CODE,
            check_in: formattedCheckIn,
            check_out: formattedCheckOut
          },
          timeout: 10000
        });

        if (mounted && response.data.success) {
          const availableRooms = response.data.availability || [];
          const isRoomAvailable = availableRooms.some(availableRoom => 
            availableRoom.id === room?.id && 
            (availableRoom.is_available === "1" || availableRoom.is_available === 1 || availableRoom.is_available === true)
          );
          
          setRoomAvailable(isRoomAvailable);
          
          if (!isRoomAvailable) {
            showSnackbar('Room not available for selected dates. Please choose different dates.', 'error');
          } else {
            showSnackbar('Room is available for selected dates!', 'success');
          }
        } else if (mounted) {
          setRoomAvailable(false);
          showSnackbar(response.data.error || 'Error checking room availability', 'error');
        }
      } catch (error) {
        console.error('Error checking availability:', error);
        if (mounted) {
          setRoomAvailable(false);
          showSnackbar('Error checking room availability. Please try again.', 'error');
        }
      } finally {
        if (mounted) setCheckingAvailability(false);
      }
    };

    const timeoutId = setTimeout(checkAvailability, 300);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [formData.checkInDate, formData.checkOutDate, room?.id, formatDateForAPI]);

  // Handle guest selection
  const handleSelectGuest = useCallback((guest) => {
    setFormData(prev => ({
      ...prev,
      title: guest.title || '',
      firstName: guest.first_name || '',
      lastName: guest.last_name || '',
      middleName: guest.middle_name || '',
      gender: guest.gender || '',
      nationality: guest.nationality || '',
      address: guest.address || '',
      country: guest.country || '',
      emergencyContactName: guest.emergency_contact_name || '',
      emergencyContactPhone: guest.emergency_contact_phone || '',
      preferences: guest.preferences || ''
    }));
    setSelectedExistingGuest(guest);
    setGuestSearchDialog(false);
    showSnackbar(`Guest information loaded for ${guest.first_name} ${guest.last_name}`, 'success');
  }, []);

  // Handle manual guest selection from dialog
  const handleManualGuestSelect = useCallback((guest) => {
    handleSelectGuest(guest);
  }, [handleSelectGuest]);

  // Clear guest selection
  const handleClearGuestSelection = useCallback(() => {
    setSelectedExistingGuest(null);
    setFormData(prev => ({
      ...prev,
      title: '',
      firstName: '',
      lastName: '',
      middleName: '',
      gender: '',
      nationality: '',
      address: '',
      country: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      preferences: ''
    }));
    showSnackbar('Guest selection cleared', 'info');
  }, []);

  const validateStep = useCallback((step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
        if (!formData.checkInDate) newErrors.checkInDate = 'Check-in date is required';
        if (!formData.checkOutDate) newErrors.checkOutDate = 'Check-out date is required';
        break;
      
      case 1:
        // Additional validation for payment step
        if (roomAvailable === false) {
          newErrors.availability = 'Room is not available for selected dates';
        }
        break;
      
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, roomAvailable]);

  const handleNext = useCallback(() => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  }, [validateStep, activeStep]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleInputChange = useCallback((field) => (event) => {
    const value = event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  }, [errors]);

  const handlePayment = useCallback(async () => {
    // FIX: Better payment initialization check
    if (!window.PaystackPop) {
      showSnackbar('Payment system is still loading. Please wait...', 'error');
      return;
    }

    if (!validateStep(1)) {
      showSnackbar('Please fix validation errors before proceeding with payment.', 'error');
      return;
    }

    setProcessingPayment(true);
    try {
      const paymentData = {
        amount: Math.round(total * 100), // Paystack expects amount in kobo
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        roomName: room?.name || 'Hotel Room'
      };

      console.log('Processing payment:', paymentData);

      const result = await api.processPayment(paymentData);
      
      if (result.success) {
        // Verify payment with backend
        let verification;
        let retries = 3;
        
        while (retries > 0) {
          try {
            verification = await api.verifyPayment(result.reference);
            if (verification.success) {
              break;
            }
          } catch (error) {
            console.log(`Verification attempt ${4 - retries} failed:`, error);
          }
          
          retries--;
          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }

        if (verification && verification.success) {
          showSnackbar('Payment processed successfully!', 'success');
          await handleSubmit(result.transactionId, result.reference);
        } else {
          throw new Error(verification?.error || 'Payment verification failed after multiple attempts');
        }
      } else {
        throw new Error(result.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Payment failed:', error);
      showSnackbar(error.message || 'Payment failed. Please try again.', 'error');
    } finally {
      setProcessingPayment(false);
    }
  }, [total, formData.email, formData.firstName, formData.lastName, room?.name, api, validateStep]);

  const handleSubmit = useCallback(async (transactionId, reference) => {
    setSubmitting(true);
    try {
      const bookingData = {
        hotel_code: HOTEL_CODE,
        room_id: room.id,
        check_in_date: formatDateForAPI(formData.checkInDate),
        check_out_date: formatDateForAPI(formData.checkOutDate),
        number_of_guests: parseInt(formData.numberOfGuests),
        number_of_rooms: parseInt(formData.numberOfRooms),
        payment_method: 'paystack',
        payment_status: 'paid',
        transaction_id: transactionId,
        payment_reference: reference,
        special_requests: formData.specialRequests,
        total_amount: total,
        adults: parseInt(formData.numberOfGuests),
        children: 0,
        source: 'website',
        guest: {
          title: formData.title,
          first_name: formData.firstName,
          last_name: formData.lastName,
          middle_name: formData.middleName,
          gender: formData.gender,
          nationality: formData.nationality,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          country: formData.country,
          emergency_contact_name: formData.emergencyContactName,
          emergency_contact_phone: formData.emergencyContactPhone,
          preferences: formData.preferences || null
        }
      };

      console.log('Submitting booking:', bookingData);

      const result = await api.createBooking(bookingData);
      
      if (result.success) {
        showSnackbar('Booking created successfully!', 'success');
        setSuccess(true);
        setActiveStep(2);
        
        setTimeout(() => {
          navigate('/RoomsSuites', { 
            state: { 
              booking: result.booking,
              guest: formData,
              room: room,
              total: total,
              nights: nights,
              transactionId: transactionId,
              reference: reference
            } 
          });
        }, 3000);
      } else {
        throw new Error(result.error || 'Failed to create booking');
      }
    } catch (error) {
      console.error('Booking failed:', error);
      showSnackbar(error.response?.data?.error || 'Booking failed. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  }, [room, formData, total, nights, api, formatDateForAPI, navigate]);

  const showSnackbar = useCallback((message, severity) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const getImageUrl = useCallback((imagePath) => {
    if (!imagePath) return null;
    
    if (imagePath.startsWith('http')) return imagePath;
    
    const serverRoot = process.env.REACT_APP_SERVER_ROOT;
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    
    return `${serverRoot}${cleanPath}`;
  }, []);

  // Memoized room features
  const roomFeatures = useMemo(() => {
    if (!room?.amenities) return [];
    
    try {
      let amenitiesArray = [];
      
      if (Array.isArray(room.amenities)) {
        amenitiesArray = room.amenities;
      } else if (typeof room.amenities === 'string') {
        amenitiesArray = room.amenities.split(',');
      } else if (typeof room.amenities === 'object') {
        amenitiesArray = Object.values(room.amenities);
      }
      
      return amenitiesArray.map(amenity => {
        const amenityStr = String(amenity).trim();
        const lowerAmenity = amenityStr.toLowerCase();
        return {
          icon: AMENITY_ICONS[lowerAmenity] || <Star />,
          label: amenityStr
        };
      });
    } catch (error) {
      console.error('Error processing amenities:', error);
      return [];
    }
  }, [room?.amenities]);

  // Helper component for review section
  const InfoRow = useCallback(({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
      <Typography variant="body2" color="text.secondary" fontWeight="600">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight="600" color="text.primary">
        {value}
      </Typography>
    </Box>
  ), []);

  // FIX: Payment button disabled state - only disable if payment is processing OR paystack failed to load
  const isPaymentButtonDisabled = processingPayment || paystackLoadError;

  const renderStepContent = useCallback((step) => {
    switch (step) {
      case 0:
        return (
          <Fade in={true} timeout={500}>
            <Box>
              <Typography
                variant="h3"
                gutterBottom
                color="primary"
                fontWeight="800"
                sx={{ mb: 4, textAlign: 'center' }}
              >
                Guest Information
              </Typography>

              {/* Guest Auto-population Section */}
              {selectedExistingGuest && (
                <Alert 
                  severity="success" 
                  sx={{ mb: 3, borderRadius: '16px', border: '1px solid', borderColor: 'success.main' }}
                  icon={<CheckCircle fontSize="large" />}
                  action={
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={handleClearGuestSelection}
                      sx={{ fontWeight: '600' }}
                    >
                      Clear
                    </Button>
                  }
                >
                  <Typography fontWeight="700">
                    🎉 Welcome back! Hope you enjoyed your previous visit {selectedExistingGuest.first_name} {selectedExistingGuest.last_name}
                  </Typography>
                </Alert>
              )}

              {foundGuests.length > 1 && (
                <Alert severity="info" sx={{ mb: 3, borderRadius: '16px' }}>
                  <Typography fontWeight="600">
                    Multiple guests found. Please select the correct one from the dialog.
                  </Typography>
                </Alert>
              )}

              <Grid container spacing={3}>
                {/* Contact Information Section */}
                <Grid item xs={12}>
                  <GlassCard>
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h5"
                        gutterBottom
                        color="primary"
                        fontWeight="700"
                        sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Box sx={{ 
                          p: 1.5, 
                          backgroundColor: 'primary.main', 
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Person sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        Contact Information
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            fullWidth
                            label="Email Address *"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange('email')}
                            error={!!errors.email}
                            helperText={errors.email || (searchingGuest ? '🔍 Searching for existing guests...' : '')}
                            variant="outlined"
                            size="medium"
                            InputProps={{
                              endAdornment: searchingGuest ? (
                                <InputAdornment position="end">
                                  <CircularProgress size={20} />
                                </InputAdornment>
                              ) : null,
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            fullWidth
                            label="Phone Number *"
                            value={formData.phone}
                            onChange={handleInputChange('phone')}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            variant="outlined"
                            size="medium"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </GlassCard>
                </Grid>

                {/* Personal Information Section */}
                <Grid item xs={12}>
                  <GlassCard>
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h5"
                        gutterBottom
                        color="primary"
                        fontWeight="700"
                        sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Box sx={{ 
                          p: 1.5, 
                          backgroundColor: 'primary.main', 
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Person sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        Personal Information
                      </Typography>
                      
                      {/* Name Row */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth sx={{ minWidth: 100 }}>
                            <InputLabel>Title</InputLabel>
                            <Select
                              value={formData.title}
                              onChange={handleInputChange('title')}
                              label="Title"
                              sx={{ borderRadius: '12px' }}
                            >
                              <MenuItem value="Mr">Mr</MenuItem>
                              <MenuItem value="Mrs">Mrs</MenuItem>
                              <MenuItem value="Miss">Miss</MenuItem>
                              <MenuItem value="Ms">Ms</MenuItem>
                              <MenuItem value="Dr">Dr</MenuItem>
                              <MenuItem value="Prof">Prof</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <StyledTextField
                            fullWidth
                            label="First Name *"
                            value={formData.firstName}
                            onChange={handleInputChange('firstName')}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                            variant="outlined"
                            size="medium"
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <StyledTextField
                            fullWidth
                            label="Last Name *"
                            value={formData.lastName}
                            onChange={handleInputChange('lastName')}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                            variant="outlined"
                            size="medium"
                          />
                        </Grid>
                      </Grid>

                      {/* Gender & Nationality Row */}
                      <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth sx={{ minWidth: 100 }}>
                            <InputLabel>Gender</InputLabel>
                            <Select
                              value={formData.gender}
                              onChange={handleInputChange('gender')}
                              label="Gender"
                              sx={{ borderRadius: '12px' }}
                            >
                              <MenuItem value="Male">Male</MenuItem>
                              <MenuItem value="Female">Female</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth sx={{ minWidth: 200 }}>
                            <InputLabel>Nationality</InputLabel>
                            <Select
                              value={formData.nationality}
                              onChange={handleInputChange('nationality')}
                              label="Nationality"
                              sx={{ borderRadius: '12px' }}
                            >
                              {COUNTRIES.map((country) => (
                                <MenuItem key={country} value={country}>
                                  {country}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      {/* Address & Country Row */}
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            fullWidth
                            label="Address"
                            value={formData.address}
                            onChange={handleInputChange('address')}
                            placeholder="Street address, City, State"
                            variant="outlined"
                            size="medium"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                         <FormControl fullWidth sx={{ minWidth: 200 }}>
                            <InputLabel>Country of Residence</InputLabel>
                            <Select
                              value={formData.country}
                              onChange={handleInputChange('country')}
                              label="Country of Residence"
                              sx={{ borderRadius: '12px' }}
                            >
                              {COUNTRIES.map((country) => (
                                <MenuItem key={country} value={country}>
                                  {country}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </GlassCard>
                </Grid>

                {/* Stay Details Section */}
                <Grid item xs={12}>
                  <GlassCard>
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h5"
                        gutterBottom
                        color="primary"
                        fontWeight="700"
                        sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Box sx={{ 
                          p: 1.5, 
                          backgroundColor: 'primary.main', 
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <CalendarToday sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        Stay Details
                      </Typography>
                      
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            fullWidth
                            label="Check-in Date *"
                            type="date"
                            value={formData.checkInDate}
                            onChange={handleInputChange('checkInDate')}
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.checkInDate}
                            helperText={errors.checkInDate}
                            variant="outlined"
                            size="medium"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            fullWidth
                            label="Check-out Date *"
                            type="date"
                            value={formData.checkOutDate}
                            onChange={handleInputChange('checkOutDate')}
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.checkOutDate}
                            helperText={errors.checkOutDate}
                            variant="outlined"
                            size="medium"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Number of Guests</InputLabel>
                            <Select
                              value={formData.numberOfGuests}
                              onChange={handleInputChange('numberOfGuests')}
                              label="Number of Guests"
                              variant="outlined"
                              size="medium"
                              sx={{ borderRadius: '12px' }}
                            >
                              {[...Array(room?.capacity || 10)].map((_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>
                                  {i + 1} {i + 1 === 1 ? 'Guest' : 'Guests'}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Maximum capacity: {room?.capacity || 10} guests
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
                            <GradientButton
                              onClick={() => {
                                // Trigger availability check
                                const checkIn = new Date(formData.checkInDate);
                                const checkOut = new Date(formData.checkOutDate);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);

                                if (checkIn < today) {
                                  setErrors(prev => ({ ...prev, checkInDate: 'Check-in date cannot be in the past' }));
                                  return;
                                }

                                if (checkOut <= checkIn) {
                                  setErrors(prev => ({ ...prev, checkOutDate: 'Check-out date must be after check-in date' }));
                                  return;
                                }

                                // Availability check will be triggered by useEffect
                              }}
                              disabled={!formData.checkInDate || !formData.checkOutDate || checkingAvailability}
                              startIcon={checkingAvailability ? <CircularProgress size={16} /> : <EventAvailable />}
                            >
                              {checkingAvailability ? 'Checking...' : 'Check Availability'}
                            </GradientButton>
                            {roomAvailable !== null && (
                              <Chip
                                label={roomAvailable ? 'Room Available' : 'Not Available'}
                                color={roomAvailable ? 'success' : 'error'}
                                variant="filled"
                                sx={{ fontWeight: '700', fontSize: '0.9rem' }}
                              />
                            )}
                          </Box>
                          {errors.availability && (
                            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                              {errors.availability}
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label="Special Requests & Preferences"
                            multiline
                            rows={4}
                            value={formData.specialRequests}
                            onChange={handleInputChange('specialRequests')}
                            placeholder="Any special requirements, dietary preferences, accessibility needs, or other requests..."
                            variant="outlined"
                            size="medium"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </GlassCard>
                </Grid>

                {/* Emergency Contact Section */}
                <Grid item xs={12}>
                  <GlassCard>
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h5"
                        gutterBottom
                        color="primary"
                        fontWeight="700"
                        sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Box sx={{ 
                          p: 1.5, 
                          backgroundColor: 'primary.main', 
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Person sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        Emergency Contact
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            fullWidth
                            label="Emergency Contact Name"
                            value={formData.emergencyContactName}
                            onChange={handleInputChange('emergencyContactName')}
                            placeholder="Full name of emergency contact"
                            variant="outlined"
                            size="medium"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            fullWidth
                            label="Emergency Contact Phone"
                            value={formData.emergencyContactPhone}
                            onChange={handleInputChange('emergencyContactPhone')}
                            placeholder="Phone number of emergency contact"
                            variant="outlined"
                            size="medium"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </GlassCard>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        );

      case 1:
        return (
          <Zoom in={true} timeout={500}>
            <Box>
              <Typography variant="h3" gutterBottom align="center" color="primary" fontWeight="800" sx={{ mb: 4 }}>
                Review & Payment
              </Typography>
              
              <Grid container spacing={3}>
                {/* Guest Information */}
                <Grid item xs={12} md={6}>
                  <GlassCard>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                        <Box sx={{ 
                          p: 1.5, 
                          backgroundColor: 'primary.main', 
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Person sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h5" color="primary" fontWeight="700">
                          Guest Information
                        </Typography>
                      </Box>
                      <Box sx={{ pl: 1 }}>
                        <InfoRow label="Full Name" value={`${formData.title} ${formData.firstName} ${formData.middleName} ${formData.lastName}`.trim()} />
                        <InfoRow label="Personal Details" value={`${formData.gender || 'Not specified'} | ${formData.nationality || 'Not provided'}`} />
                        <InfoRow label="Contact" value={`${formData.email} | ${formData.phone}`} />
                        <InfoRow label="Address" value={`${formData.address || 'Not provided'}, ${formData.country || ''}`.trim()} />
                      </Box>
                    </CardContent>
                  </GlassCard>
                </Grid>

                {/* Stay Information */}
                <Grid item xs={12} md={6}>
                  <GlassCard>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                        <Box sx={{ 
                          p: 1.5, 
                          backgroundColor: 'primary.main', 
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <CalendarToday sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h5" color="primary" fontWeight="700">
                          Stay Details
                        </Typography>
                      </Box>
                      <Box sx={{ pl: 1 }}>
                        <InfoRow label="Duration" value={`${formData.checkInDate} to ${formData.checkOutDate}`} />
                        <InfoRow label="Nights" value={nights.toString()} />
                        <InfoRow label="Guests" value={`${formData.numberOfGuests} guest(s), ${formData.numberOfRooms} room(s)`} />
                        <InfoRow label="Special Requests" value={formData.specialRequests || 'None'} />
                      </Box>
                    </CardContent>
                  </GlassCard>
                </Grid>

                {/* Payment Method */}
                <Grid item xs={12}>
                  <GlassCard>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                        <Box sx={{ 
                          p: 1.5, 
                          backgroundColor: 'primary.main', 
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <CreditCard sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h5" color="primary" fontWeight="700">
                          Payment Method
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: 4, 
                        border: '3px solid',
                        borderColor: 'primary.main',
                        borderRadius: '20px',
                        background: `linear-gradient(135deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8f9fa', 0.9)} 100%)`,
                        textAlign: 'center',
                        mb: 3
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, gap: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 32,
                              backgroundColor: '#0066CC',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem' }}>
                              PSK
                            </Typography>
                          </Box>
                          <Typography variant="h5" fontWeight="800" color="primary">
                            Paystack Payment
                          </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                          Secure payment via Paystack gateway
                        </Typography>

                        {/* FIX: Better payment button state handling */}
                        {paystackLoadError ? (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            Payment system failed to load. Please refresh the page.
                          </Alert>
                        ) : null}

                        <GradientButton
                          fullWidth
                          size="large"
                          onClick={handlePayment}
                          disabled={isPaymentButtonDisabled}
                          startIcon={processingPayment ? <CircularProgress size={20} /> : <Payment />}
                          sx={{ maxWidth: 300, mx: 'auto' }}
                        >
                          {processingPayment ? 'Processing...' : `Pay ₦${total}`}
                        </GradientButton>
                        
                        {!window.PaystackPop && !paystackLoadError && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Payment system loading...
                          </Typography>
                        )}
                      </Box>

                      {/* Room availability warning */}
                      {roomAvailable === false && (
                        <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                          <Typography fontWeight="600">
                            Room is not available for the selected dates. Please go back and select different dates.
                          </Typography>
                        </Alert>
                      )}
                    </CardContent>
                  </GlassCard>
                </Grid>

                {/* Cost Summary */}
                <Grid item xs={12}>
                  <GlassCard>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h5" gutterBottom color="primary" fontWeight="700" sx={{ mb: 4 }}>
                        Cost Summary
                      </Typography>
                      <Box sx={{ pl: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Box>
                            <Typography variant="h6" fontWeight="700" color="primary">
                              {room?.name}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                              {nights} nights × ₦{room?.price || room?.price_per_night || room?.rate || 0}/night
                            </Typography>
                          </Box>
                          <Typography variant="h6" fontWeight="700" color="primary">
                            ₦{total}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 3, borderWidth: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h4" fontWeight="800">
                            Total Amount
                          </Typography>
                          <Typography variant="h3" fontWeight="800" color="primary">
                            ₦{total}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </GlassCard>
                </Grid>
              </Grid>
            </Box>
          </Zoom>
        );

      case 2:
        return (
          <Fade in={true} timeout={500}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Box sx={{ 
                width: 120, 
                height: 120, 
                backgroundColor: 'success.light', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 4
              }}>
                <CheckCircle sx={{ fontSize: 60, color: 'success.main' }} />
              </Box>
              <Typography variant="h2" gutterBottom color="success.main" fontWeight="800">
                Booking Confirmed!
              </Typography>
              <Typography variant="h5" color="text.secondary" sx={{ mb: 6, maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}>
                Thank you for your booking. Your reservation has been confirmed and payment processed successfully.
              </Typography>
              
              <GlassCard sx={{ maxWidth: 500, mx: 'auto', p: 4, textAlign: 'left' }}>
                <Typography variant="h4" gutterBottom fontWeight="800" color="primary" sx={{ mb: 3 }}>
                  Booking Summary
                </Typography>
                <InfoRow label="Booking Reference" value={`#${Math.random().toString(36).substr(2, 8).toUpperCase()}`} />
                <InfoRow label="Room" value={room?.name} />
                <InfoRow label="Check-in" value={formData.checkInDate} />
                <InfoRow label="Check-out" value={formData.checkOutDate} />
                <Divider sx={{ my: 3, borderWidth: 2 }} />
                <InfoRow label="Total Paid" value={`₦${total}`} />
              </GlassCard>

              <GradientButton
                size="large"
                onClick={() => navigate('/')}
                sx={{ mt: 6, px: 8, py: 2, fontSize: '1.2rem' }}
              >
                Return to Home
              </GradientButton>
            </Box>
          </Fade>
        );

      default:
        return null;
    }
  }, [
    activeStep, formData, errors, searchingGuest, selectedExistingGuest, foundGuests, 
    roomAvailable, checkingAvailability, room, nights, total, isPaymentButtonDisabled,
    paystackLoadError, handleInputChange, handleClearGuestSelection, handlePayment,
    InfoRow, navigate
  ]);

  // Guest Selection Dialog
  const renderGuestSelectionDialog = useCallback(() => (
    <Dialog 
      open={guestSearchDialog} 
      onClose={() => setGuestSearchDialog(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '20px', background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Person color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="700" color="primary">
              Select Guest
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Multiple guests found. Please select the correct one:
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <List sx={{ pt: 2 }}>
          {foundGuests.map((guest) => (
            <ListItem 
              key={guest.id}
              button
              onClick={() => handleManualGuestSelect(guest)}
              sx={{
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: '16px',
                mb: 2,
                p: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="h6" fontWeight="600" color="primary">
                    {guest.title} {guest.first_name} {guest.last_name}
                  </Typography>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      ✉️ {guest.email}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      📞 {guest.phone}
                    </Typography>
                    {guest.nationality && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        🌍 {guest.nationality}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={() => setGuestSearchDialog(false)}
          sx={{ borderRadius: '12px', px: 4 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleClearGuestSelection}
          variant="outlined"
          sx={{ borderRadius: '12px', px: 4 }}
        >
          Create New Guest
        </Button>
      </DialogActions>
    </Dialog>
  ), [guestSearchDialog, foundGuests, handleManualGuestSelect, handleClearGuestSelection]);

  if (!room) {
    navigate('/');
    return null;
  }

  const fallbackImage = 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';

  return (
    <Box sx={{ 
      bgcolor: 'background.default', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
    }}>
      {/* Top Bar */}
      <TopBar />

      {/* Navigation */}
      <NavigationBar />

      {/* Main Content */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h1" gutterBottom fontWeight="800" color="primary" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              Complete Your Booking
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', fontWeight: '500' }}>
              Finalize your reservation 
            </Typography>
          </Box>

          {success && (
            <Alert severity="success" sx={{ mb: 4, borderRadius: '16px', border: '1px solid', borderColor: 'success.main' }}>
              <Typography fontWeight="700">
                Booking confirmed successfully! Redirecting to confirmation...
              </Typography>
            </Alert>
          )}

          <StyledStepper activeStep={activeStep} sx={{ mb: 8, px: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </StyledStepper>

          <Grid container spacing={4} justifyContent="center">
            {/* Booking Form */}
            <Grid item xs={12} lg={8}>
              <GlassCard sx={{ p: { xs: 3, md: 4 } }}>
                {renderStepContent(activeStep)}

                {activeStep < 2 && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mt: 6, 
                    pt: 4, 
                    borderTop: `2px solid ${alpha('#000', 0.1)}` 
                  }}>
                    <Button
                      onClick={handleBack}
                      disabled={activeStep === 0}
                      variant="outlined"
                      size="large"
                      sx={{ borderRadius: '16px', px: 6, py: 1.5, fontWeight: '600' }}
                    >
                      Back
                    </Button>

                    {activeStep === steps.length - 2 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body1" color="text.secondary" fontWeight="600">
                            Total Amount
                          </Typography>
                          <Typography variant="h3" color="primary" fontWeight="800">
                            ₦{total}
                          </Typography>
                        </Box>
                        <GradientButton
                          onClick={handlePayment}
                          disabled={isPaymentButtonDisabled}
                          size="large"
                          startIcon={processingPayment ? <CircularProgress size={20} /> : <Payment />}
                          sx={{ px: 6, py: 2, fontSize: '1.1rem' }}
                        >
                          {processingPayment ? 'Processing...' : 'Proceed to Payment'}
                        </GradientButton>
                      </Box>
                    ) : (
                      <GradientButton
                        onClick={handleNext}
                        size="large"
                        sx={{ px: 6, py: 1.5 }}
                      >
                        Continue
                      </GradientButton>
                    )}
                  </Box>
                )}
              </GlassCard>
            </Grid>

            {/* Room Summary */}
            <Grid item xs={12} lg={4}>
              <Card
                sx={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                  border: '2px solid',
                  borderColor: 'primary.main',
                }}
              >
                <CardMedia
                  component="img"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                  image={
                    room.images && room.images[0]
                      ? getImageUrl(room.images[0])
                      : fallbackImage
                  }
                  alt={room.name}
                  onError={(e) => {
                    e.target.src = fallbackImage;
                  }}
                />

                <Box sx={{ p: 3 }}>
                  <Typography variant="h5" fontWeight="800" gutterBottom color="primary">
                    {room.name}
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ mb: 2, lineHeight: 1.6 }}
                  >
                    {room.type} • {room.capacity} guests • {room.size || 'Standard'} size
                  </Typography>

                  {/* Room Features */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight="700" gutterBottom color="primary">
                      Room Features:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {roomFeatures.length > 0 ? (
                        roomFeatures.map((feature, index) => (
                          <FeatureChip
                            key={index}
                            icon={feature.icon}
                            label={feature.label}
                            size="small"
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No features listed
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Booking Summary */}
                  <Divider sx={{ my: 2, borderWidth: 1.5 }} />

                  {formData.checkInDate && formData.checkOutDate ? (
                    <Box sx={{ mb: 2 }}>
                      {[
                        { label: 'Check-in', value: new Date(formData.checkInDate).toLocaleDateString() },
                        { label: 'Check-out', value: new Date(formData.checkOutDate).toLocaleDateString() },
                        { label: 'Nights', value: nights },
                        { label: 'Guests', value: formData.numberOfGuests },
                        { label: 'Price per night', value: `₦${room?.price || room?.price_per_night || room?.rate || 0}` },
                      ].map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="600">{item.label}:</Typography>
                          <Typography variant="body2" fontWeight="700">{item.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                      Select dates to see pricing details
                    </Typography>
                  )}

                  <Divider sx={{ my: 2, borderWidth: 1.5 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="800">
                      Total:
                    </Typography>
                    <Typography variant="h5" color="primary" fontWeight="800">
                      ₦{total}
                    </Typography>
                  </Box>

                  {nights > 0 && (
                    <Typography variant="body2" color="text.secondary" textAlign="right" fontWeight="600">
                      for {nights} night{nights > 1 ? 's' : ''}
                    </Typography>
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />

      {/* Guest Selection Dialog */}
      {renderGuestSelectionDialog()}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            borderRadius: '16px',
            fontWeight: '600',
            fontSize: '1rem'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingPage;